import { apiClient, ApiError } from '$lib/api/client';
import {
  authHeader,
  startSession,
  updateAccount,
  type Account,
  type Session
} from '$lib/stores/auth';

/**
 * Registering, signing in, and recovering a password.
 *
 * # Registering does not sign you in
 *
 * ainapi creates the account and returns no token: a new account cannot be used
 * until somebody approves it by hand. The reply is a message saying so, and the
 * screen's next state is "we will be in touch" rather than the signed-in app.
 * See docs/refactor/accounts.md.
 *
 * # The errors are the interesting part
 *
 * Every failure here is one a customer has to be told something specific about,
 * and the API is careful about which: a wrong password and an unknown address
 * are deliberately the same answer, an unapproved account is a different one,
 * and a reset link that has expired is a third. `authError` turns the code the
 * API sends into a translation key so a screen never has to parse prose.
 */

/** What ainapi answers a successful sign-in with. */
interface SessionResponse {
  token: string;
  expires_at: string;
  user: Account;
}

/** What ainapi answers a registration with: an account, and no token. */
interface RegistrationResponse {
  message: string;
  user: Account;
}

interface MessageResponse {
  message: string;
}

/** The shape of every failure ainapi returns from these routes. */
interface ErrorBody {
  error?: string;
  code?: string;
}

/**
 * A failure a login screen can act on.
 *
 * `key` is a translation key rather than a message, so the wording lives with
 * the rest of the app's text and the API stays free to reword its own.
 */
export class AuthError extends Error {
  constructor(
    public key: string,
    public status: number
  ) {
    super(key);
    this.name = 'AuthError';
  }
}

/**
 * The translation key for a failed call.
 *
 * Driven by the `code` field rather than the status, because two of these share
 * a status and mean different things to the customer. Anything unrecognised
 * falls back to a general message — a screen that renders a raw server string
 * is one that will eventually show somebody a stack trace.
 */
function authError(error: unknown): AuthError {
  if (!(error instanceof ApiError)) {
    return new AuthError('auth.errors.network', 0);
  }

  const body = (error.data ?? {}) as ErrorBody;
  switch (body.code) {
    case 'BAD_CREDENTIALS':
      return new AuthError('auth.errors.badCredentials', error.status);
    case 'ACCOUNT_NOT_APPROVED':
      return new AuthError('auth.errors.notApproved', error.status);
    case 'EMAIL_TAKEN':
      return new AuthError('auth.errors.emailTaken', error.status);
    case 'RESET_TOKEN_INVALID':
      return new AuthError('auth.errors.resetTokenInvalid', error.status);
    case 'VALIDATION_ERROR':
      return new AuthError('auth.errors.validation', error.status);
    default:
      // 400 with no code we know is a binding failure: the form let something
      // through that the API's own rules refused.
      if (error.status === 400) return new AuthError('auth.errors.validation', error.status);
      return new AuthError('auth.errors.general', error.status);
  }
}

/**
 * Sign in, and remember the session.
 *
 * The session is stored before this returns, so a caller can navigate straight
 * away and find the app already signed in.
 */
export async function signIn(email: string, password: string): Promise<Session> {
  try {
    const { data } = await apiClient.post<SessionResponse>(
      '/auth/login',
      { email, password },
      // One attempt. A retried sign-in is a second password guess against a
      // rate-limited endpoint, and the failures worth retrying — 5xx — are not
      // ones a customer should wait through three times.
      { retries: 0 }
    );

    const next: Session = {
      token: data.token,
      expiresAt: data.expires_at,
      account: data.user
    };
    startSession(next);
    return next;
  } catch (error) {
    throw authError(error);
  }
}

export interface Registration {
  email: string;
  password: string;
  fullName?: string;
  company?: string;
}

/**
 * Create an account, which cannot be used until it is approved.
 *
 * Returns the message the API chose, which says so in the customer's language
 * — the API writes it, because it is the API that knows an account waits.
 */
export async function register(details: Registration): Promise<RegistrationResponse> {
  try {
    const { data } = await apiClient.post<RegistrationResponse>(
      '/auth/register',
      {
        email: details.email,
        password: details.password,
        full_name: details.fullName || undefined,
        company: details.company || undefined
      },
      { retries: 0 }
    );
    return data;
  } catch (error) {
    throw authError(error);
  }
}

/**
 * Ask for a password reset link.
 *
 * Succeeds whether or not the address has an account — the API answers the same
 * way either way, so that posting addresses at it does not reveal who has one.
 * The screen must say the same thing this does: that a link has been sent *if*
 * the address is registered.
 */
export async function requestPasswordReset(email: string): Promise<MessageResponse> {
  try {
    const { data } = await apiClient.post<MessageResponse>(
      '/auth/password-reset',
      { email },
      { retries: 0 }
    );
    return data;
  } catch (error) {
    throw authError(error);
  }
}

/** Set a new password with the token from the email. */
export async function resetPassword(token: string, password: string): Promise<MessageResponse> {
  try {
    const { data } = await apiClient.post<MessageResponse>(
      '/auth/password-reset/confirm',
      { token, password },
      { retries: 0 }
    );
    return data;
  } catch (error) {
    throw authError(error);
  }
}

/**
 * Re-read the signed-in account, and store what comes back.
 *
 * The balance moves as credits are spent, and the approval flag can be revoked,
 * so this is how a long-lived tab catches up without signing in again. It
 * carries the token explicitly rather than relying on any interceptor: this app
 * has none, and a request that silently acquired a credential would be one
 * nobody could audit from the call site.
 */
export async function refreshAccount(): Promise<Account> {
  try {
    const { data } = await apiClient.get<Account>('/auth/me', undefined, {
      headers: authHeader(),
      retries: 0
    });
    updateAccount(data);
    return data;
  } catch (error) {
    throw authError(error);
  }
}
